// =============================================================================
// DocumentTypeChips.jsx — closing-document type tags for a document.
//
// Three pieces, used by DocumentsPanel:
//   • DocumentTypeChips  — read-only chip row shown under a file name. Applied
//                          types are solid; AI *suggestions* are dashed with
//                          inline confirm/dismiss. A "review split" hint appears
//                          when the file looks like several documents.
//   • DocumentTagModal   — confirm / add / remove a document's type tags.
//   • SplitDocumentModal — the opt-in, user-reviewed page-range split.
//
// The app auto-applies only HIGH-confidence types (server-side, conservative);
// everything else surfaces here as a suggestion the user opts into. Splitting is
// never automatic — it always runs through the review modal below.
// =============================================================================

import { useState, useEffect } from 'react';
import { B, radius } from '../../constants/brand';
import { api } from '../../api/client';
import { Chip, Btn, Select, Modal, ModalFooter, InfoNote, useToast, Icon } from '../ui';
import { isPdf } from '../../utils/docViewer';
import { sanitizeNumericInput, WHOLE } from '../../utils/numericInput';

// ─── Taxonomy (fetched once, cached for the session) ─────────────────────────
let _taxonomyCache = null;
export async function loadDocTypeTaxonomy() {
  if (_taxonomyCache) return _taxonomyCache;
  try {
    const r = await api.get('document-type-taxonomy');
    _taxonomyCache = Array.isArray(r?.types) ? r.types : [];
  } catch { _taxonomyCache = []; }
  return _taxonomyCache;
}

// ─── Split candidate detection (client mirror of docTypes.isSplitCandidate) ───
// True when ≥2 distinct types occupy disjoint page ranges — a batch scan of
// separate documents, as opposed to one compound document.
export function splitCandidate(doc) {
  const ranges = (doc?.types || []).filter(t => t.pageStart && t.pageEnd);
  if (ranges.length < 2) return false;
  if (new Set(ranges.map(t => t.typeCode)).size < 2) return false;
  const sorted = ranges.slice().sort((a, b) => a.pageStart - b.pageStart);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].pageStart <= sorted[i - 1].pageEnd) return false; // overlap ⇒ compound, not a split
  }
  return true;
}

function confTitle(t) {
  const src = t.source === 'user' ? 'You' : t.source === 'heuristic' ? 'Auto' : 'AI';
  const pct = t.confidence != null ? ` · ${Math.round(t.confidence * 100)}%` : '';
  const pages = t.pageStart && t.pageEnd
    ? ` · p${t.pageStart}${t.pageEnd !== t.pageStart ? `-${t.pageEnd}` : ''}`
    : '';
  return `${src}${pct}${pages}`;
}

// A dashed "suggested" pill with inline confirm (✓) / dismiss (✕).
const SuggestedChip = ({ t, onConfirm, onDismiss }) => (
  <span title={`Suggested — ${confTitle(t)}`}
    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, borderRadius: radius.pill,
      padding: '2px 4px 2px 9px', fontSize: 11, fontWeight: 600, color: B.amber,
      background: 'transparent', border: `1px dashed ${B.amber}` }}>
    {t.label}
    <button onClick={(e) => { e.stopPropagation(); onConfirm(); }} title="Confirm this type"
      style={{ display: 'flex', background: 'none', border: 'none', cursor: 'pointer', color: B.green, padding: 1 }}>
      <Icon name="check" size={13} />
    </button>
    <button onClick={(e) => { e.stopPropagation(); onDismiss(); }} title="Dismiss"
      style={{ display: 'flex', background: 'none', border: 'none', cursor: 'pointer', color: B.textLt, padding: 1 }}>
      <Icon name="close" size={12} />
    </button>
  </span>
);

export const DocumentTypeChips = ({ doc, enabled, onApply, onRemove, onEdit, onSplit }) => {
  const types    = doc.types || [];
  const applied  = types.filter(t => t.isApplied);
  const suggested = types.filter(t => t.suggested);
  const showAdd  = enabled;
  if (!types.length && !showAdd) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginTop: 4 }}>
      {applied.map(t => (
        <span key={t.typeCode} title={confTitle(t)}>
          <Chip color={B.tealDk} onRemove={() => onRemove(doc.documentID, t.typeCode)}
            style={{ fontSize: 11, padding: '2px 4px 2px 9px' }}>
            {t.label}
          </Chip>
        </span>
      ))}
      {suggested.map(t => (
        <SuggestedChip key={t.typeCode} t={t}
          onConfirm={() => onApply(doc.documentID, t.typeCode)}
          onDismiss={() => onRemove(doc.documentID, t.typeCode)} />
      ))}
      {showAdd && (
        <button onClick={(e) => { e.stopPropagation(); onEdit(doc); }} title="Edit document types"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'none',
            border: `1px dashed ${B.border}`, borderRadius: radius.pill, padding: '2px 9px',
            fontSize: 11, fontWeight: 600, color: B.textMd, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Icon name={applied.length || suggested.length ? 'edit' : 'add'} size={11} />
          {applied.length || suggested.length ? 'Edit types' : 'Add type'}
        </button>
      )}
      {splitCandidate(doc) && isPdf(doc.fileName) && (
        <button onClick={(e) => { e.stopPropagation(); onSplit(doc); }}
          title="This looks like several documents scanned together"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: B.navy + '10',
            border: `1px solid ${B.navy}33`, borderRadius: radius.pill, padding: '2px 9px',
            fontSize: 11, fontWeight: 600, color: B.navy, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Icon name="layers" size={11} /> Review split
        </button>
      )}
    </div>
  );
};

// ─── Tag editor modal ────────────────────────────────────────────────────────

export const DocumentTagModal = ({ doc, onApply, onRemove, onClose, onOpenSplit }) => {
  const [taxonomy, setTaxonomy] = useState([]);
  const [addCode, setAddCode]   = useState('');
  const [busy, setBusy]         = useState(false);

  useEffect(() => { loadDocTypeTaxonomy().then(setTaxonomy); }, []);
  if (!doc) return null;

  const types     = doc.types || [];
  const applied   = types.filter(t => t.isApplied);
  const suggested = types.filter(t => t.suggested);
  const presentCodes = new Set(types.map(t => t.typeCode));
  const addable = taxonomy.filter(t => !presentCodes.has(t.code));

  const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };

  return (
    <Modal title="Document types" width={520} onClose={onClose}>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 4, wordBreak: 'break-word' }}>{doc.fileName}</div>
      <InfoNote>The app auto-applies types it's confident about. Confirm suggestions or add types by hand — your choices always win over re-classification.</InfoNote>

      {/* Applied */}
      <div style={{ fontSize: 11, fontWeight: 700, color: B.textMd, letterSpacing: 0.5, textTransform: 'uppercase', margin: '14px 0 6px' }}>Applied</div>
      {applied.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {applied.map(t => (
            <span key={t.typeCode} title={confTitle(t)}>
              <Chip color={B.tealDk} onRemove={busy ? undefined : () => run(() => onRemove(doc.documentID, t.typeCode))}>{t.label}</Chip>
            </span>
          ))}
        </div>
      ) : <div style={{ fontSize: 13, color: B.textLt }}>No types applied yet.</div>}

      {/* Suggested */}
      {suggested.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: B.textMd, letterSpacing: 0.5, textTransform: 'uppercase', margin: '16px 0 6px' }}>Suggested</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {suggested.map(t => (
              <div key={t.typeCode} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1, fontSize: 13, color: B.text }}>{t.label}
                  <span style={{ fontSize: 11, color: B.textLt, marginLeft: 6 }}>{confTitle(t)}</span>
                </span>
                <Btn size="sm" variant="secondary" disabled={busy} onClick={() => run(() => onApply(doc.documentID, t.typeCode))}>Confirm</Btn>
                <Btn size="sm" variant="ghost" disabled={busy} onClick={() => run(() => onRemove(doc.documentID, t.typeCode))}>Dismiss</Btn>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add */}
      <div style={{ fontSize: 11, fontWeight: 700, color: B.textMd, letterSpacing: 0.5, textTransform: 'uppercase', margin: '16px 0 6px' }}>Add a type</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <Select value={addCode} onChange={setAddCode}
            options={[{ value: '', label: 'Select a type…' },
              ...addable.map(t => ({ value: t.code, label: `${t.label} (${t.group})` }))]} />
        </div>
        <Btn disabled={!addCode || busy} style={{ marginBottom: 16 }}
          onClick={() => run(async () => { await onApply(doc.documentID, addCode); setAddCode(''); })}>Add</Btn>
      </div>

      <ModalFooter>
        {isPdf(doc.fileName) && (
          <Btn variant="ghost" onClick={() => onOpenSplit(doc)} style={{ marginRight: 'auto' }}>
            <Icon name="layers" size={14} /> Split document…
          </Btn>
        )}
        <Btn variant="ghost" onClick={onClose}>Done</Btn>
      </ModalFooter>
    </Modal>
  );
};

// ─── Split review modal ──────────────────────────────────────────────────────

let _segKey = 0;
const nextKey = () => `seg-${++_segKey}`;

export const SplitDocumentModal = ({ doc, onClose, onDone }) => {
  const { toast } = useToast();
  const [taxonomy, setTaxonomy] = useState([]);
  const [segments, setSegments] = useState([]);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState(null);

  useEffect(() => { loadDocTypeTaxonomy().then(setTaxonomy); }, []);

  // Seed from the detected disjoint type ranges, else one empty segment.
  useEffect(() => {
    if (!doc) return;
    const ranges = (doc.types || []).filter(t => t.pageStart && t.pageEnd)
      .slice().sort((a, b) => a.pageStart - b.pageStart);
    setSegments(ranges.length
      ? ranges.map(t => ({ key: nextKey(), pageStart: t.pageStart, pageEnd: t.pageEnd, typeCode: t.typeCode, fileName: '' }))
      : [{ key: nextKey(), pageStart: 1, pageEnd: 1, typeCode: '', fileName: '' }]);
  }, [doc]);

  if (!doc) return null;

  const update = (key, patch) => setSegments(prev => prev.map(s => s.key === key ? { ...s, ...patch } : s));
  const addRow = () => setSegments(prev => [...prev, { key: nextKey(), pageStart: '', pageEnd: '', typeCode: '', fileName: '' }]);
  const removeRow = (key) => setSegments(prev => prev.filter(s => s.key !== key));

  const submit = async () => {
    setErr(null);
    const payload = segments.map(s => ({
      pageStart: parseInt(s.pageStart, 10),
      pageEnd:   parseInt(s.pageEnd, 10),
      typeCode:  s.typeCode || undefined,
      fileName:  s.fileName?.trim() || undefined,
    }));
    if (payload.some(s => !Number.isFinite(s.pageStart) || !Number.isFinite(s.pageEnd) || s.pageStart < 1 || s.pageEnd < s.pageStart)) {
      setErr('Every segment needs a valid page range (start ≤ end).');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`documents/${doc.documentID}/split`, { segments: payload });
      toast.success(`Created ${r.created?.length || 0} document${r.created?.length === 1 ? '' : 's'}. The original is kept.`);
      onDone && await onDone();
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const typeOptions = [{ value: '', label: 'Type (optional)' }, ...taxonomy.map(t => ({ value: t.code, label: t.label }))];

  return (
    <Modal title="Split into separate documents" width={640} onClose={onClose}>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 4, wordBreak: 'break-word' }}>{doc.fileName}</div>
      <InfoNote>Each range below becomes its own document (re-OCR'd and re-classified). The original file is kept — nothing is deleted.</InfoNote>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
        {segments.map((s, i) => (
          <div key={s.key} style={{ display: 'grid', gridTemplateColumns: '70px 70px 1fr 1.4fr 32px', gap: 8, alignItems: 'center' }}>
            <input type="text" inputMode="numeric" value={s.pageStart} placeholder="from"
              onChange={e => update(s.key, { pageStart: sanitizeNumericInput(e.target.value, WHOLE) })}
              style={numStyle} />
            <input type="text" inputMode="numeric" value={s.pageEnd} placeholder="to"
              onChange={e => update(s.key, { pageEnd: sanitizeNumericInput(e.target.value, WHOLE) })}
              style={numStyle} />
            <select value={s.typeCode} onChange={e => update(s.key, { typeCode: e.target.value })} style={selStyle}>
              {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <input value={s.fileName} placeholder="File name (optional)"
              onChange={e => update(s.key, { fileName: e.target.value })} style={txtStyle} />
            <button onClick={() => removeRow(s.key)} disabled={segments.length <= 1} title="Remove segment"
              style={{ background: 'none', border: 'none', cursor: segments.length <= 1 ? 'default' : 'pointer',
                color: segments.length <= 1 ? B.border : B.red, display: 'flex', padding: 4 }}>
              <Icon name="trash" size={15} />
            </button>
          </div>
        ))}
      </div>

      <button onClick={addRow}
        style={{ marginTop: 10, background: 'none', border: `1px dashed ${B.border}`, borderRadius: radius.md,
          padding: '7px 12px', fontSize: 13, fontWeight: 600, color: B.navy, cursor: 'pointer', fontFamily: 'inherit',
          display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <Icon name="add" size={13} /> Add segment
      </button>

      {err && <div style={{ color: B.red, fontSize: 12, marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="warning" size={13} /> {err}</div>}

      <ModalFooter>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn onClick={submit} loading={busy} disabled={busy}>
          Split into {segments.length} document{segments.length === 1 ? '' : 's'}
        </Btn>
      </ModalFooter>
    </Modal>
  );
};

const numStyle = { width: '100%', padding: '7px 8px', border: `1px solid ${B.border}`, borderRadius: radius.md,
  fontSize: 13, fontFamily: 'inherit', color: B.text, background: B.surface, outline: 'none', boxSizing: 'border-box' };
const selStyle = { ...numStyle, cursor: 'pointer' };
const txtStyle = { ...numStyle };
